// src/pages/admin/AdminCRM.jsx
// CRM interne — cockpit campagne Art Advisors (septembre). Branché sur `crm_leads`
// (RLS admin/team). 3 onglets : « À traiter aujourd'hui » (relances dues), « Pipeline »
// (Kanban), « Campagne email » (référentiel des mails + calendrier + automatisations).
// Chaque action commerciale est journalisée dans `crm_activities`. Contacts d'acquisition
// (email, linkedin_url, pays, segment, signaux de scoring) stockés dans crm_leads — stack
// Supabase seule, zéro SaaS (plan de lancement sept. 2026). Accès RLS admin/team uniquement.
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { CAMPAIGN, CALENDAR, EMAIL_GROUPS, AUTOMATION_RULES } from "../../data/crmEmailCampaign";

const STAGES = [
  { id: "lead", label: "Lead capté", hint: "Formulaire, webinaire, pub" },
  { id: "qualifie", label: "Qualifié", hint: "Persona confirmé" },
  { id: "demo", label: "Démo programmée", hint: "Créneau réservé" },
  { id: "essai", label: "Essai en cours", hint: "Compte créé" },
  { id: "client", label: "Client payant", hint: "Stripe confirmé" },
  { id: "perdu", label: "Perdu", hint: "Non converti" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.id, s.label]));
const FUNNEL = ["lead", "qualifie", "demo", "essai", "client"];

const SOURCES = [
  { value: "linkedin_ads", label: "LinkedIn Ads", color: "#C9A961" },
  { value: "webinaire", label: "Webinaire", color: "#8B9A7D" },
  { value: "art_basel_paris", label: "Art Basel Paris", color: "#A9714A" },
  { value: "ecole_partenaire", label: "École partenaire", color: "#6E7B8B" },
  { value: "parrainage", label: "Parrainage", color: "#B5838D" },
  { value: "autre", label: "Autre", color: "#6B6B6B" },
];
const PERSONAS = [
  { value: "non_qualifie", label: "Non qualifié" },
  { value: "camille", label: "Camille (indépendante)" },
  { value: "marc", label: "Marc (établi)" },
];
const PERSONA_LABEL = Object.fromEntries(PERSONAS.map((p) => [p.value, p.label]));

const NEXT_ACTIONS = [
  { value: "appeler", label: "Appeler" },
  { value: "message_linkedin", label: "Message LinkedIn" },
  { value: "email", label: "Envoyer un email" },
  { value: "proposer_demo", label: "Proposer une démo" },
  { value: "confirmer_demo", label: "Confirmer une démo" },
  { value: "relance_webinaire", label: "Relancer après le webinaire" },
  { value: "accompagner_essai", label: "Accompagner l'essai" },
  { value: "verifier_activation", label: "Vérifier l'activation" },
  { value: "relance_avant_fin_essai", label: "Relancer avant la fin de l'essai" },
  { value: "cloturer_perdu", label: "Clôturer comme perdu" },
];
const NEXT_ACTION_LABEL = Object.fromEntries(NEXT_ACTIONS.map((a) => [a.value, a.label]));

const WEBINAR = [
  { value: "", label: "—" },
  { value: "registered", label: "Inscrit" },
  { value: "attended", label: "Présent" },
  { value: "absent", label: "Absent" },
];
const WEBINAR_LABEL = { registered: "Inscrit", attended: "Présent", absent: "Absent" };

const sourceOf = (v) => SOURCES.find((s) => s.value === v) || { label: v, color: "#6B6B6B" };
const DAY = 86400000;
const daysSince = (d) => (d ? Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / DAY)) : null);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—");
const toLocalInput = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const off = dt.getTimezoneOffset() * 60000;
  return new Date(dt.getTime() - off).toISOString().slice(0, 16);
};

const SEGMENTS = [
  { value: "art_advisor", label: "Art Advisor" },
  { value: "notaire", label: "Notaire" },
  { value: "family_office", label: "Family Office" },
  { value: "collector", label: "Collectionneur" },
  { value: "insurance", label: "Assurance" },
  { value: "gallery", label: "Galerie" },
  { value: "partner", label: "Partenaire" },
  { value: "press", label: "Presse" },
];

// Signaux ARTKIV-ready (§9 du plan de lancement) — alimentent le score de façon transparente.
const SCORING_SIGNALS = [
  { key: "sig_family_office", label: "Family Office / private wealth", pts: 3 },
  { key: "sig_succession", label: "Succession / transmission", pts: 3 },
  { key: "sig_collection_management", label: "Collection management", pts: 2 },
  { key: "sig_valuation", label: "Valuation / appraisal", pts: 2 },
  { key: "sig_insurance", label: "Assurance / conservation", pts: 2 },
  { key: "sig_ai_interest", label: "Intérêt IA / technologie", pts: 1 },
];

// Score = signaux + LinkedIn (+1) + multi-clients (+3) + advisor qualifié (+3).
function computeSuggestedScore(f) {
  let s = SCORING_SIGNALS.reduce((acc, sig) => acc + (f[sig.key] ? sig.pts : 0), 0);
  if (String(f.linkedin_url || "").trim()) s += 1;
  if (Number(f.clients_count) >= 2) s += 3;
  if (f.persona === "camille" || f.persona === "marc") s += 3;
  return s;
}

const EMPTY_FORM = {
  full_name: "", structure: "", persona: "non_qualifie", clients_count: "",
  source: "linkedin_ads", segment: "art_advisor", email: "", linkedin_url: "",
  job_title: "", country: "", why_artkiv: "", notes: "",
  sig_family_office: false, sig_succession: false, sig_collection_management: false,
  sig_valuation: false, sig_insurance: false, sig_ai_interest: false,
};

const LEAD_COLS =
  "id, full_name, structure, persona, segment, clients_count, source, stage, stage_updated_at, notes, created_at, " +
  "email, linkedin_url, job_title, country, why_artkiv, " +
  "next_action_at, next_action_type, last_contact_at, lead_score, lost_reason, webinar_status, email_step, owned_by";

export default function AdminCRM() {
  const { user } = useAuth();
  const [tab, setTab] = useState("today");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterPersona, setFilterPersona] = useState("Tous");
  const [savingId, setSavingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [openAction, setOpenAction] = useState(null); // lead id whose next-action editor is open

  async function fetchLeads() {
    setLoading(true);
    setError("");
    const { data, error: e } = await supabase.from("crm_leads").select(LEAD_COLS).order("stage_updated_at", { ascending: false });
    if (e) { setError(e.message); setLeads([]); }
    else setLeads(data || []);
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, []);

  async function logActivity(leadId, activity_type, metadata = {}) {
    await supabase.from("crm_activities").insert({
      lead_id: leadId, activity_type, campaign_code: CAMPAIGN.code, metadata, created_by: user?.id || null,
    });
  }

  async function patchLead(lead, patch, activity, meta) {
    setSavingId(lead.id);
    const { error: e } = await supabase.from("crm_leads").update(patch).eq("id", lead.id);
    if (e) alert("Erreur : " + e.message);
    else if (activity) await logActivity(lead.id, activity, meta || {});
    await fetchLeads();
    setSavingId(null);
  }

  function moveStage(lead, newStage) {
    if (newStage === lead.stage) return;
    const patch = { stage: newStage };
    if (newStage === "perdu") {
      const reason = window.prompt("Motif de perte (optionnel) :", lead.lost_reason || "");
      if (reason !== null) patch.lost_reason = reason || null;
    }
    patchLead(lead, patch, "stage_change", { from: lead.stage, to: newStage });
    setOpenAction(newStage === "client" || newStage === "perdu" ? null : lead.id);
  }

  const markContacted = (lead) =>
    patchLead(lead, { last_contact_at: new Date().toISOString() }, "manual_contact");

  const saveNextAction = (lead, type, when) =>
    patchLead(lead, { next_action_type: type || null, next_action_at: when ? new Date(when).toISOString() : null }, "next_action_set", { type, when });

  const adjustScore = (lead, delta) =>
    patchLead(lead, { lead_score: Math.max(0, (lead.lead_score || 0) + delta) }, "score_adjust", { delta });

  const setWebinar = (lead, status) =>
    patchLead(lead, {
      webinar_status: status || null,
      ...(status === "registered" ? { webinar_registered_at: new Date().toISOString() } : {}),
      ...(status === "attended" ? { webinar_attended_at: new Date().toISOString() } : {}),
    }, "webinar_status", { status });

  async function addLead(e) {
    e.preventDefault();
    if (!form.full_name.trim()) { alert("Nom requis."); return; }
    setAdding(true);
    const { data, error: err } = await supabase.from("crm_leads").insert({
      full_name: form.full_name.trim(),
      structure: form.structure.trim() || null,
      persona: form.persona,
      segment: form.segment,
      clients_count: form.clients_count === "" ? null : Number(form.clients_count),
      source: form.source,
      email: form.email.trim().toLowerCase() || null,
      linkedin_url: form.linkedin_url.trim() || null,
      job_title: form.job_title.trim() || null,
      country: form.country.trim() || null,
      why_artkiv: form.why_artkiv.trim() || null,
      notes: form.notes.trim() || null,
      lead_score: computeSuggestedScore(form),
      sig_family_office: form.sig_family_office,
      sig_succession: form.sig_succession,
      sig_collection_management: form.sig_collection_management,
      sig_valuation: form.sig_valuation,
      sig_insurance: form.sig_insurance,
      sig_ai_interest: form.sig_ai_interest,
      campaign_code: CAMPAIGN.code,
      owned_by: user?.id || null,
    }).select("id").single();
    setAdding(false);
    if (err) { alert("Erreur : " + err.message); return; }
    if (data?.id) await logActivity(data.id, "lead_created", { source: form.source });
    setForm(EMPTY_FORM);
    setShowAdd(false);
    fetchLeads();
  }

  const filtered = useMemo(
    () => leads.filter((c) => filterPersona === "Tous" || c.persona === filterPersona),
    [leads, filterPersona]
  );

  // KPIs pipeline
  const kpis = useMemo(() => {
    const total = leads.length;
    const active = leads.filter((l) => l.stage !== "perdu");
    const reached = (s) => active.filter((l) => FUNNEL.indexOf(l.stage) >= FUNNEL.indexOf(s)).length;
    const rate = (a, b) => (reached(b) > 0 ? Math.round((reached(a) / reached(b)) * 100) : 0);
    return {
      total,
      essais: leads.filter((l) => l.stage === "essai").length,
      clients: leads.filter((l) => l.stage === "client").length,
      conv: active.length > 0 ? Math.round((reached("client") / active.length) * 100) : 0,
      funnel: [
        { label: "Lead → Qualifié", v: rate("qualifie", "lead") },
        { label: "Qualifié → Démo", v: rate("demo", "qualifie") },
        { label: "Démo → Essai", v: rate("essai", "demo") },
        { label: "Essai → Client", v: rate("client", "essai") },
      ],
    };
  }, [leads]);

  // Alertes « À traiter aujourd'hui »
  const alertsOf = (l) => {
    const now = Date.now();
    const stageAt = new Date(l.stage_updated_at || l.created_at).getTime();
    const created = new Date(l.created_at).getTime();
    const lastContact = l.last_contact_at ? new Date(l.last_contact_at).getTime() : null;
    const nextAt = l.next_action_at ? new Date(l.next_action_at).getTime() : null;
    const closed = l.stage === "client" || l.stage === "perdu";
    const out = [];
    if (l.stage === "lead" && !lastContact && now - created > DAY) out.push("Nouveau lead non contacté (>24 h)");
    if (l.stage === "qualifie" && now - stageAt > 3 * DAY) out.push("Qualifié sans démo (>3 j)");
    if (l.stage === "demo" && !nextAt && now - stageAt > 2 * DAY) out.push("Démo sans suite (>48 h)");
    if (nextAt && nextAt < now && !closed) out.push("Relance en retard");
    if (l.stage === "essai" && now - stageAt > 7 * DAY) out.push("Essai à risque (>7 j)");
    if (!closed && now - Math.max(stageAt, lastContact || 0) > 14 * DAY) out.push("Prospect bloqué (>14 j)");
    return out;
  };
  const todo = useMemo(() => {
    return filtered
      .map((l) => ({ lead: l, alerts: alertsOf(l) }))
      .filter((x) => x.alerts.length > 0)
      .sort((a, b) => (b.lead.lead_score || 0) - (a.lead.lead_score || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered]);

  return (
    <div className="min-h-screen bg-[#F9F7F4] text-[#1A1A1A]" style={{ padding: "40px 32px" }}>
      <div className="max-w-[1400px] mx-auto">
        {/* En-tête */}
        <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.14em] uppercase text-[#C9A961] mb-2" style={{ fontFamily: "Georgia, serif" }}>
              Artkiv Partners — Cockpit commercial
            </div>
            <h1 className="text-3xl md:text-4xl font-normal" style={{ fontFamily: "Georgia, serif" }}>CRM — Campagne Art Advisors</h1>
            <div className="text-sm text-gray-500 mt-1">Pipeline + relances + campagne email · contacts &amp; scoring sur Supabase (accès admin/team)</div>
          </div>
          <button onClick={() => setShowAdd((v) => !v)} className="bg-[#1A1A1A] text-white text-sm px-5 py-2.5 rounded-md hover:bg-black transition">
            {showAdd ? "Fermer" : "+ Ajouter un lead"}
          </button>
        </div>

        {/* Onglets */}
        <div className="flex gap-1 border-b border-[#E4DFD5] mb-6">
          {[
            { id: "today", label: `À traiter aujourd'hui${todo.length ? ` · ${todo.length}` : ""}` },
            { id: "pipeline", label: "Pipeline" },
            { id: "emails", label: "Campagne email" },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`text-sm px-4 py-2.5 -mb-px border-b-2 transition ${tab === t.id ? "border-[#1A1A1A] text-[#1A1A1A] font-medium" : "border-transparent text-gray-500 hover:text-[#1A1A1A]"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Formulaire d'ajout */}
        {showAdd && (
          <form onSubmit={addLead} className="mb-6 bg-white border border-[#EAE5D9] rounded-lg p-6 grid md:grid-cols-3 gap-4">
            <Field label="Nom *"><input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="crm-in" required /></Field>
            <Field label="Structure"><input value={form.structure} onChange={(e) => setForm({ ...form, structure: e.target.value })} placeholder="Indépendante · Paris" className="crm-in" /></Field>
            <Field label="Fonction"><input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Art Advisor" className="crm-in" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="crm-in" /></Field>
            <Field label="LinkedIn"><input value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} placeholder="linkedin.com/in/…" className="crm-in" /></Field>
            <Field label="Pays"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="France" className="crm-in" /></Field>
            <Field label="Segment"><select value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} className="crm-in">{SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
            <Field label="Persona"><select value={form.persona} onChange={(e) => setForm({ ...form, persona: e.target.value })} className="crm-in">{PERSONAS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}</select></Field>
            <Field label="Clients gérés"><input type="number" min="0" value={form.clients_count} onChange={(e) => setForm({ ...form, clients_count: e.target.value })} className="crm-in" /></Field>
            <Field label="Source"><select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="crm-in">{SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>

            {/* Signaux ARTKIV-ready → score transparent (§9) */}
            <div className="md:col-span-3">
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">Signaux ARTKIV-ready</label>
              <div className="flex flex-wrap gap-x-5 gap-y-2">
                {SCORING_SIGNALS.map((sig) => (
                  <label key={sig.key} className="flex items-center gap-1.5 text-[13px] text-gray-700">
                    <input type="checkbox" checked={form[sig.key]} onChange={(e) => setForm({ ...form, [sig.key]: e.target.checked })} />
                    {sig.label} <span className="text-gray-400">(+{sig.pts})</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="md:col-span-3"><Field label="Pourquoi ARTKIV (note humaine)"><textarea value={form.why_artkiv} onChange={(e) => setForm({ ...form, why_artkiv: e.target.value })} rows={2} placeholder="Gère plusieurs collections privées, travaille avec des Family Offices, publie sur la transmission…" className="crm-in" /></Field></div>
            <div className="md:col-span-3"><Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="crm-in" /></Field></div>

            <div className="md:col-span-3 flex items-center justify-between gap-4 border-t border-[#F0ECE2] pt-4">
              <div className="text-sm text-gray-600">
                Score suggéré : <ScoreBadge score={computeSuggestedScore(form)} />
                <span className="text-xs text-gray-400 ml-2">{computeSuggestedScore(form) >= 10 ? "Priorité A" : computeSuggestedScore(form) >= 6 ? "Priorité B" : "Priorité C"}</span>
              </div>
              <button type="submit" disabled={adding} className="bg-[#1A1A1A] text-white text-sm px-5 py-2 rounded-md hover:bg-black transition disabled:opacity-50">{adding ? "Ajout…" : "Créer le lead"}</button>
            </div>
          </form>
        )}

        {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}

        {/* Filtre persona (hors onglet emails) */}
        {tab !== "emails" && (
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <span className="text-xs uppercase tracking-wide text-gray-500">Filtrer</span>
            {["Tous", "camille", "marc", "non_qualifie"].map((p) => (
              <button key={p} onClick={() => setFilterPersona(p)}
                className={`text-sm px-4 py-1.5 rounded-sm border transition ${filterPersona === p ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#D8D2C4] text-[#1A1A1A] hover:bg-white"}`}>
                {p === "Tous" ? "Tous" : PERSONA_LABEL[p]}
              </button>
            ))}
          </div>
        )}

        {/* ---- ONGLET : À TRAITER AUJOURD'HUI ---- */}
        {tab === "today" && (
          <div>
            {loading ? <Muted>Chargement…</Muted> : todo.length === 0 ? (
              <div className="bg-white border border-[#EAE5D9] rounded-lg p-8 text-center text-gray-500">
                Rien à relancer pour l'instant. 🎉<br /><span className="text-xs">Les leads non contactés, relances en retard et essais à risque apparaîtront ici.</span>
              </div>
            ) : (
              <div className="space-y-3">
                {todo.map(({ lead, alerts }) => (
                  <div key={lead.id} className="bg-white border border-[#EAE5D9] rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-[220px]">
                        <div className="flex items-center gap-2">
                          <span className="text-[15px]" style={{ fontFamily: "Georgia, serif" }}>{lead.full_name}</span>
                          <ScoreBadge score={lead.lead_score} />
                        </div>
                        <div className="text-xs text-gray-500">{lead.structure || "—"} · {PERSONA_LABEL[lead.persona]} · {STAGE_LABEL[lead.stage]}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {alerts.map((a) => (
                            <span key={a} className="text-[11px] px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">{a}</span>
                          ))}
                        </div>
                        <div className="mt-2 text-[11px] text-gray-400">
                          Dernier contact : {fmtDate(lead.last_contact_at)} · Prochaine action : {lead.next_action_type ? `${NEXT_ACTION_LABEL[lead.next_action_type] || lead.next_action_type} (${fmtDate(lead.next_action_at)})` : "aucune"}
                          {lead.owned_by === user?.id ? " · à vous" : ""}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-stretch min-w-[220px]">
                        <div className="flex gap-2">
                          <button disabled={savingId === lead.id} onClick={() => markContacted(lead)} className="crm-btn-light flex-1">Contacté aujourd'hui</button>
                          <button onClick={() => setOpenAction(openAction === lead.id ? null : lead.id)} className="crm-btn-light">Prochaine action</button>
                        </div>
                        <select value={lead.stage} disabled={savingId === lead.id} onChange={(e) => moveStage(lead, e.target.value)} className="crm-in text-xs">
                          {STAGES.map((s) => <option key={s.id} value={s.id}>Déplacer → {s.label}</option>)}
                        </select>
                      </div>
                    </div>
                    {openAction === lead.id && <NextActionEditor lead={lead} onSave={saveNextAction} saving={savingId === lead.id} />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ---- ONGLET : PIPELINE ---- */}
        {tab === "pipeline" && (
          <div>
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E4DFD5] border border-[#E4DFD5] mb-4">
              {[{ label: "Leads (total)", value: kpis.total }, { label: "En essai", value: kpis.essais }, { label: "Clients payants", value: kpis.clients }, { label: "Conversion globale", value: `${kpis.conv} %`, hint: "clients / actifs" }].map((k) => (
                <div key={k.label} className="bg-white px-6 py-5">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">{k.label}</div>
                  <div className="text-3xl" style={{ fontFamily: "Georgia, serif" }}>{loading ? "…" : k.value}</div>
                  {k.hint && <div className="text-xs text-gray-400 mt-1">{k.hint}</div>}
                </div>
              ))}
            </div>
            {/* Taux de conversion par étape */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E4DFD5] border border-[#E4DFD5] mb-8">
              {kpis.funnel.map((f) => (
                <div key={f.label} className="bg-white px-5 py-4">
                  <div className="text-[11px] text-gray-500 mb-1">{f.label}</div>
                  <div className="text-xl" style={{ fontFamily: "Georgia, serif" }}>{loading ? "…" : `${f.v} %`}</div>
                </div>
              ))}
            </div>

            {/* Kanban */}
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(0,1fr))` }}>
              {STAGES.map((stage) => {
                const items = filtered.filter((c) => c.stage === stage.id);
                const muted = stage.id === "perdu";
                return (
                  <div key={stage.id}>
                    <div className={`border-b-2 pb-2.5 mb-4 ${muted ? "border-gray-300" : "border-[#1A1A1A]"}`}>
                      <div className="text-base" style={{ fontFamily: "Georgia, serif", color: muted ? "#9A9A9A" : "#1A1A1A" }}>{stage.label}</div>
                      <div className="text-[11px] text-gray-400">{stage.hint} · {items.length}</div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {items.map((c) => {
                        const src = sourceOf(c.source);
                        return (
                          <div key={c.id} className="bg-white border border-[#EAE5D9] rounded-sm p-4">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-[15px]" style={{ fontFamily: "Georgia, serif" }}>{c.full_name}</span>
                              <ScoreBadge score={c.lead_score} />
                            </div>
                            {c.structure && <div className="text-xs text-gray-500 mb-1">{c.structure}</div>}
                            {c.clients_count != null && <div className="text-xs text-gray-600 mb-1">{c.clients_count} clients gérés</div>}
                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                              <span className="text-[10.5px] px-2 py-0.5 rounded-sm bg-[#F9F7F4]" style={{ color: src.color, border: `1px solid ${src.color}` }}>{src.label}</span>
                              {c.webinar_status && <span className="text-[10.5px] px-2 py-0.5 rounded-sm bg-[#F4F1EA] text-gray-600 border border-[#E4DFD5]">🎥 {WEBINAR_LABEL[c.webinar_status] || c.webinar_status}</span>}
                              <span className="text-[11px] text-gray-400 ml-auto">{daysSince(c.stage_updated_at) ?? 0} j</span>
                            </div>
                            {c.next_action_type && (
                              <div className="text-[11px] text-gray-600 mb-2 bg-[#F9F7F4] rounded px-2 py-1 border border-[#EFEADE]">
                                ▸ {NEXT_ACTION_LABEL[c.next_action_type] || c.next_action_type} · {fmtDate(c.next_action_at)}
                              </div>
                            )}
                            <div className="flex items-center gap-1 mb-2">
                              <button onClick={() => adjustScore(c, -5)} disabled={savingId === c.id} className="crm-chip">−5</button>
                              <button onClick={() => adjustScore(c, 5)} disabled={savingId === c.id} className="crm-chip">+5</button>
                              <select value={c.webinar_status || ""} disabled={savingId === c.id} onChange={(e) => setWebinar(c, e.target.value)} className="crm-in text-[11px] py-1 flex-1">
                                {WEBINAR.map((w) => <option key={w.value} value={w.value}>Webinaire : {w.label}</option>)}
                              </select>
                            </div>
                            <div className="flex gap-1 mb-2">
                              <button onClick={() => markContacted(c)} disabled={savingId === c.id} className="crm-btn-light flex-1 text-[11px]">Contacté</button>
                              <button onClick={() => setOpenAction(openAction === c.id ? null : c.id)} className="crm-btn-light text-[11px]">Action</button>
                            </div>
                            {openAction === c.id && <NextActionEditor lead={c} onSave={saveNextAction} saving={savingId === c.id} compact />}
                            <select value={c.stage} disabled={savingId === c.id} onChange={(e) => moveStage(c, e.target.value)} className="crm-in text-xs mt-1">
                              {STAGES.map((s) => <option key={s.id} value={s.id}>Déplacer → {s.label}</option>)}
                            </select>
                          </div>
                        );
                      })}
                      {items.length === 0 && <div className="text-xs text-[#C4C0B4] italic py-3">Aucun contact</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ---- ONGLET : CAMPAGNE EMAIL ---- */}
        {tab === "emails" && <EmailCampaign />}

        <div className="mt-12 pt-5 border-t border-[#E4DFD5] text-[11.5px] text-gray-400">
          Alimentation : sourcing LinkedIn / webinaire (<code>/art-advisor/webinar</code> → <code>webinar-register</code>, dédup email+LinkedIn),
          activités → <code>crm_activities</code> (clics, ouvertures, inscriptions), Stripe (passage payant → étape « Client »). Chaque action est journalisée.
          <br /><strong>RGPD :</strong> tout envoi de prospection doit <strong>exclure les leads dont <code>unsubscribed_at</code> est renseigné</strong> (opt-out obligatoire) ;
          <code>webinar-register</code> respecte déjà cette opposition (pas de réactivation ni d'email à un désinscrit).
        </div>
      </div>

      <style>{`
        .crm-in{width:100%;border:1px solid #D6D0C4;border-radius:6px;padding:6px 10px;font-size:13px;background:#fff}
        .crm-in:focus{outline:none;border-color:#C9A961}
        .crm-btn-light{border:1px solid #D6D0C4;background:#fff;border-radius:6px;padding:6px 10px;font-size:12px;transition:background .15s}
        .crm-btn-light:hover{background:#F4F1EA}
        .crm-btn-light:disabled{opacity:.5}
        .crm-chip{border:1px solid #D6D0C4;background:#fff;border-radius:6px;padding:3px 8px;font-size:11px}
        .crm-chip:disabled{opacity:.5}
      `}</style>
    </div>
  );
}

/* --- petits composants --- */
function Field({ label, children }) {
  return (<div><label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</label>{children}</div>);
}
function Muted({ children }) { return <div className="text-sm text-gray-400 py-6">{children}</div>; }
function ScoreBadge({ score }) {
  const s = score || 0;
  const tone = s >= 50 ? "bg-[#1A1A1A] text-white" : s >= 25 ? "bg-[#C9A961] text-white" : "bg-[#EFEADE] text-gray-600";
  return <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${tone}`} title="Score commercial">{s} pts</span>;
}

function NextActionEditor({ lead, onSave, saving, compact }) {
  const [type, setType] = useState(lead.next_action_type || "appeler");
  const [when, setWhen] = useState(toLocalInput(lead.next_action_at) || toLocalInput(new Date(Date.now() + DAY)));
  return (
    <div className={`mt-2 p-3 rounded-md bg-[#F9F7F4] border border-[#EFEADE] ${compact ? "" : "flex flex-wrap items-end gap-2"}`}>
      <div className={compact ? "mb-2" : ""}>
        <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Type</label>
        <select value={type} onChange={(e) => setType(e.target.value)} className="crm-in text-xs">
          {NEXT_ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      <div className={compact ? "mb-2" : ""}>
        <label className="block text-[10px] uppercase tracking-wide text-gray-500 mb-1">Quand</label>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="crm-in text-xs" />
      </div>
      <button disabled={saving} onClick={() => onSave(lead, type, when)} className="bg-[#1A1A1A] text-white text-xs px-4 py-2 rounded-md hover:bg-black disabled:opacity-50">
        {saving ? "…" : "Planifier"}
      </button>
    </div>
  );
}

function EmailCampaign() {
  const [open, setOpen] = useState(null);
  const copy = (email) => {
    const txt = `Objet : ${email.objectA}${email.objectB ? `\nObjet B : ${email.objectB}` : ""}\nPréheader : ${email.preheader || ""}\n\n${email.body.join("\n\n")}\n\nCTA : ${email.ctaPrimary}${email.ctaSecondary ? ` / ${email.ctaSecondary}` : ""}`;
    if (navigator.clipboard) navigator.clipboard.writeText(txt);
  };
  return (
    <div className="space-y-8">
      {/* Résumé campagne */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-6 grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-[#C9A961] mb-1">Webinaire</div>
          <div className="text-lg" style={{ fontFamily: "Georgia, serif" }}>{CAMPAIGN.webinar.title}</div>
          <div className="text-sm text-gray-600 mt-1">{CAMPAIGN.webinar.date} · {CAMPAIGN.webinar.time} · {CAMPAIGN.webinar.duration}</div>
          <div className="text-sm text-gray-600 mt-3"><strong>Offre :</strong> {CAMPAIGN.offer.name} — {CAMPAIGN.offer.price}. {CAMPAIGN.offer.trial}.</div>
          <div className="text-xs text-gray-400 mt-2">Code campagne : <code>{CAMPAIGN.code}</code> · utm_campaign=<code>{CAMPAIGN.utm.campaign}</code></div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-[#C9A961] mb-2">Objectifs de la cohorte</div>
          <div className="grid grid-cols-2 gap-2">
            {CAMPAIGN.objectives.map((o) => (
              <div key={o.step} className="bg-[#F9F7F4] rounded px-3 py-2 border border-[#EFEADE]">
                <div className="text-xl" style={{ fontFamily: "Georgia, serif" }}>{o.target}</div>
                <div className="text-[11px] text-gray-500">{o.step}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Calendrier */}
      <div>
        <h3 className="text-lg mb-3" style={{ fontFamily: "Georgia, serif" }}>Calendrier maître</h3>
        <div className="space-y-4">
          {CALENDAR.map((phase) => (
            <div key={phase.phase} className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-[#F4F1EA] text-sm font-medium">{phase.phase}</div>
              <div className="divide-y divide-[#F0ECE2]">
                {phase.rows.map((r, i) => (
                  <div key={i} className="px-4 py-2 flex items-center gap-4 text-sm">
                    <span className="text-gray-500 w-28 shrink-0">{r.date}</span>
                    <span className="text-gray-400 w-16 shrink-0 text-right">{r.time}</span>
                    <span>{r.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Emails */}
      <div>
        <h3 className="text-lg mb-3" style={{ fontFamily: "Georgia, serif" }}>Contenus des emails</h3>
        <div className="space-y-6">
          {EMAIL_GROUPS.map((g) => (
            <div key={g.id}>
              <div className="text-sm font-medium text-gray-700 mb-2">{g.title}</div>
              <div className="space-y-2">
                {g.emails.map((email) => {
                  const key = g.id + email.id;
                  const isOpen = open === key;
                  return (
                    <div key={key} className="bg-white border border-[#EAE5D9] rounded-lg">
                      <button onClick={() => setOpen(isOpen ? null : key)} className="w-full text-left px-4 py-3 flex items-center gap-3">
                        <span className="text-xs font-mono px-2 py-0.5 rounded bg-[#1A1A1A] text-white shrink-0">{email.id}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{email.objectA}</div>
                          <div className="text-[11px] text-gray-400">{email.when} · {email.segment}</div>
                        </div>
                        <span className="text-gray-400 text-xs">{isOpen ? "▲" : "▼"}</span>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-4 border-t border-[#F0ECE2] pt-3 text-sm">
                          <div className="grid md:grid-cols-2 gap-2 text-[12px] text-gray-600 mb-3">
                            <div><strong>Objet A :</strong> {email.objectA}</div>
                            {email.objectB && <div><strong>Objet B :</strong> {email.objectB}</div>}
                            {email.preheader && <div className="md:col-span-2"><strong>Préheader :</strong> {email.preheader}</div>}
                            {email.utmContent && <div><strong>utm_content :</strong> <code>{email.utmContent}</code></div>}
                            <div><strong>CTA :</strong> {email.ctaPrimary}{email.ctaSecondary ? ` / ${email.ctaSecondary}` : ""}</div>
                          </div>
                          <div className="space-y-2 text-gray-800 leading-relaxed">
                            {email.body.map((p, i) => <p key={i}>{p}</p>)}
                          </div>
                          {email.crmAction && <div className="mt-3 text-[11px] text-[#8B7B4A] bg-[#FBF7EC] border border-[#EFE4C7] rounded px-3 py-2">⚙️ CRM : {email.crmAction}</div>}
                          <button onClick={() => copy(email)} className="mt-3 crm-btn-light text-xs">Copier le contenu</button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Automatisations */}
      <div>
        <h3 className="text-lg mb-3" style={{ fontFamily: "Georgia, serif" }}>Règles d'automatisation (à câbler côté plateforme d'envoi)</h3>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden divide-y divide-[#F0ECE2]">
          {AUTOMATION_RULES.map((r) => (
            <div key={r.event} className="px-4 py-2.5 flex flex-col md:flex-row md:items-center gap-1 md:gap-4 text-sm">
              <span className="md:w-64 shrink-0 text-gray-700 font-medium">{r.event}</span>
              <span className="text-gray-600">{r.action}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
